import { useState, useEffect, useRef } from 'react';
import {
  Calendar, MessageSquare, BarChart2, Megaphone,
  Wrench, FileText, ArrowRight, Check,
  Menu, X, ChevronRight, Mail, Phone,
  Sparkles, Shield, Zap, Clock, Users, CheckCircle2,
} from 'lucide-react';
import './App.css';

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26" aria-hidden>
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.853L.057 24l6.304-1.653A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.003-1.366l-.359-.213-3.732.979.997-3.638-.235-.374A9.818 9.818 0 1112 21.818zm5.389-7.545c-.297-.148-1.757-.867-2.03-.966-.272-.099-.47-.148-.669.149-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.074-.297-.148-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.457.13-.605.134-.133.297-.347.446-.52.148-.174.198-.298.297-.497.1-.198.05-.372-.024-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.57-.01s-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.57-.085 1.757-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    </svg>
  );
}

/* ─── helpers ─── */

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─── Navbar ─── */

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setOpen(false);
  };

  const NAV = [
    { id: 'services',      label: 'Services' },
    { id: 'how-it-works',  label: 'How It Works' },
    { id: 'pricing',       label: 'Pricing' },
    { id: 'testimonials',  label: 'Testimonials' },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <button className="nav-logo" onClick={() => go('hero')}>
          <span className="logo-gem">◈</span>
          <span className="logo-name">Chandelia</span>
        </button>

        <div className={`nav-links ${open ? 'open' : ''}`}>
          {NAV.map(({ id, label }) => (
            <button key={id} onClick={() => go(id)} className="nav-link">{label}</button>
          ))}
          <button onClick={() => go('contact')} className="nav-cta">
            Book a Free Consultation <ChevronRight size={14} />
          </button>
        </div>

        {/* persistent mobile CTA — stays visible whether or not the menu is open */}
        <button onClick={() => go('contact')} className="nav-cta-mobile">
          Hire a VA
        </button>

        <button className="hamburger" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </nav>
  );
}

/* ─── Hero ─── */

function Hero() {
  const [alive, setAlive] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAlive(true), 80); return () => clearTimeout(t); }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="hero" className="hero">
      {/* subtle gradient/texture backdrop (replaces floating particles) */}
      <div className="hero-bg" aria-hidden />
      <div className="orb orb-1" aria-hidden />
      <div className="orb orb-2" aria-hidden />

      <div className="hero-row">
        {/* copy — left column */}
        <div className={`hero-copy ${alive ? 'in' : ''}`}>
          <div className="hero-pill">
            <Sparkles size={12} /> Premium Virtual Support
          </div>

          <h1 className="hero-h1">
            Delegate More.<br />
            <span className="gold-text">Scale Faster.</span>
          </h1>

          <p className="hero-sub">
            Reclaim your week. Elite virtual specialists handle your inbox,
            calendar, and operations — so you lead without the drag.
          </p>

          {/* one primary + one secondary CTA, unified button style */}
          <div className="hero-btns">
            <button className="btn-gold" onClick={() => go('contact')}>
              Book a Free Consultation <ArrowRight size={16} />
            </button>
            <button className="btn-secondary" onClick={() => go('how-it-works')}>
              See How It Works <ArrowRight size={14} />
            </button>
          </div>

          {/* single consolidated trust + availability line */}
          <div className="hero-onboarding">
            <span className="onboarding-dot" />
            Now onboarding new clients — vetted specialists matched to you within 24 hours.
          </div>
        </div>

        {/* visual — right column */}
        <div className={`hero-visual ${alive ? 'in' : ''}`}>
          <div className="hero-photo">
            <img
              src={`${process.env.PUBLIC_URL}/human-iamge.png`}
              alt="A Chandelia virtual specialist supporting a client from her desk"
              loading="eager"
            />
          </div>
          <div className="hero-visual-accent" aria-hidden />
        </div>
      </div>
    </section>
  );
}

/* ─── Services ─── */

const SERVICES = [
  { Icon: Calendar,      title: 'Executive Assistance', desc: 'Calendar management, inbox zero, travel booking, and high-level coordination handled flawlessly.' },
  { Icon: MessageSquare, title: 'Customer Support',     desc: 'Professional teams that represent your brand with speed, empathy, and deep expertise.' },
  { Icon: BarChart2,     title: 'Data & Research',      desc: 'Market intelligence, data entry, competitor analysis, and custom reporting on demand.' },
  { Icon: Megaphone,     title: 'Social Media',         desc: 'Strategy, content creation, scheduling, and community engagement across all platforms.' },
  { Icon: Wrench,        title: 'Technical Support',    desc: 'Remote helpdesk, software onboarding, and troubleshooting for your team and customers.' },
  { Icon: FileText,      title: 'Content & Copy',       desc: 'SEO articles, newsletters, product descriptions, and marketing copy that converts.' },
];

function Services() {
  const [ref, inView] = useInView();
  return (
    <section id="services" className="services section-pad" ref={ref}>
      <div className="container">
        <div className={`sec-header ${inView ? 'in' : ''}`}>
          <span className="sec-label">What We Do</span>
          <h2>Support That Scales<br />With Your Business</h2>
          <p>From daily tasks to complex projects, our specialists take the load off your plate.</p>
        </div>
        <div className={`srv-grid ${inView ? 'in' : ''}`}>
          {SERVICES.map(({ Icon, title, desc }, i) => (
            <div key={title} className="srv-card" style={{ transitionDelay: `${i * 0.07}s` }}>
              <div className="srv-icon-wrap"><Icon size={22} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
              <span className="srv-arrow"><ArrowRight size={16} /></span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ─── */

const STEPS = [
  { num: '01', Icon: MessageSquare, title: 'Tell Us Your Needs', desc: 'Share your challenges and goals. We listen closely to understand your workflow, industry, and timezone needs.' },
  { num: '02', Icon: Shield,        title: 'Meet Your Team',     desc: 'We match you with pre-vetted specialists curated for your industry, communication style, and tools stack.' },
  { num: '03', Icon: Zap,           title: 'Watch It Flow',      desc: 'Your team integrates into your existing processes and delivers results from day one. No ramp-up drama.' },
];

function HowItWorks() {
  const [ref, inView] = useInView();
  return (
    <section id="how-it-works" className="how-it-works section-pad" ref={ref}>
      <div className="hiw-glow-1" aria-hidden />
      <div className="hiw-glow-2" aria-hidden />
      <div className="container">
        <div className={`hiw-grid ${inView ? 'in' : ''}`}>
          <div className="hiw-media">
            <img
              src={`${process.env.PUBLIC_URL}/people-colaborating.png`}
              alt="A team collaborating in a modern office"
              loading="lazy"
            />
          </div>

          <div className="hiw-content">
            <div className="sec-header light">
              <span className="sec-label light">The Process</span>
              <h2>Up & Running in<br />3 Simple Steps</h2>
              <p>No lengthy onboarding. No lock-in contracts. Just seamless support from day one.</p>
            </div>

            <div className="steps-col">
              {STEPS.map(({ num, Icon, title, desc }) => (
                <div key={title} className="hiw-step">
                  <div className="hiw-step-icon"><Icon size={20} /></div>
                  <div className="hiw-step-body">
                    <span className="hiw-step-num">Step {num}</span>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Why Us ─── */

const WHY = [
  { Icon: Clock,  title: 'Same-Day Onboarding', desc: 'From sign-up to your first task completed — all within 24 hours.' },
  { Icon: Shield, title: 'Vetted Specialists',  desc: 'Every specialist passes a rigorous multi-stage screening before joining.' },
  { Icon: Zap,    title: 'Flexible & Scalable', desc: 'Scale up or down instantly. Add specialists, pause, or cancel anytime.' },
  { Icon: Users,  title: 'Dedicated Manager',   desc: 'A dedicated account manager ensures quality, consistency, and alignment.' },
];

function WhyUs() {
  const [ref, inView] = useInView();
  return (
    <section className="why-us section-pad" ref={ref}>
      <div className="container">
        <div className={`sec-header ${inView ? 'in' : ''}`}>
          <span className="sec-label">Why Chandelia</span>
          <h2>Built Different,<br />By Design</h2>
        </div>
        <div className={`why-grid ${inView ? 'in' : ''}`}>
          {WHY.map(({ Icon, title, desc }, i) => (
            <div key={title} className="why-card" style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="why-icon"><Icon size={20} /></div>
              <h4>{title}</h4>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing (#8 nav item) ─── */

const PLANS = [
  {
    name: 'Starter', price: 'RWF 150,000', period: '/month',
    desc: 'Perfect for solopreneurs and early-stage founders.',
    features: ['40 hours/month', '1 dedicated VA specialist', 'Email & calendar management', 'Basic research & data entry', 'WhatsApp & email support', '48hr response SLA'],
    cta: 'Get Started', highlight: false,
  },
  {
    name: 'Growth', price: 'RWF 350,000', period: '/month',
    desc: 'For scaling teams that need comprehensive support.',
    features: ['100 hours/month', '2 dedicated VA specialists', 'All Starter features', 'Customer support handling', 'Social media management', '4hr response SLA', 'Dedicated account manager'],
    cta: 'Get Started', highlight: true,
  },
  {
    name: 'Enterprise', price: 'Custom', period: 'pricing',
    desc: 'Full-service support for established businesses.',
    features: ['Unlimited hours', 'Full VA specialist team', 'All Growth features', 'Executive-level assistants', 'Custom SLA agreement', 'On-site option available', 'Priority 24/7 coverage'],
    cta: 'Contact Us', highlight: false,
  },
];

function Pricing() {
  const [ref, inView] = useInView();
  return (
    <section id="pricing" className="pricing section-pad" ref={ref}>
      <div className="pricing-bg" aria-hidden />
      <div className="container">
        <div className={`sec-header ${inView ? 'in' : ''}`}>
          <span className="sec-label">Pricing</span>
          <h2>Simple, Transparent<br />Pricing</h2>
          <p>No hidden fees. No lock-in contracts. Scale up or down at any time.</p>
        </div>
        <div className={`pricing-grid ${inView ? 'in' : ''}`}>
          {PLANS.map(({ name, price, period, desc, features, cta, highlight }, i) => (
            <div
              key={name}
              className={`price-card ${highlight ? 'price-highlight' : ''}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {highlight && <div className="price-badge">Most Popular</div>}
              <div className="price-header">
                <h3>{name}</h3>
                <p className="price-desc">{desc}</p>
                <div className="price-amount">
                  <span className="price-num">{price}</span>
                  <span className="price-period">{period}</span>
                </div>
              </div>
              <ul className="price-features">
                {features.map(f => (
                  <li key={f}><CheckCircle2 size={15} className="check-icon" />{f}</li>
                ))}
              </ul>
              <button
                className={`price-cta ${highlight ? 'btn-gold' : 'btn-ghost'}`}
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {cta} <ArrowRight size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */

const TESTIMONIALS = [
  { initials: 'EM', name: 'Eric Mugisha',   role: 'CEO, Savanna Digital',       color: '#4f46e5', quote: 'Chandelia transformed how I operate. My VA manages my inbox and schedule daily — I get back hours every week to focus on growth.' },
  { initials: 'AU', name: 'Aline Uwimana',  role: 'Founder, Umurava Consulting', color: '#0891b2', quote: "Our customer response time dropped significantly after onboarding Chandelia's support team. Clients notice the difference immediately." },
  { initials: 'PN', name: 'Patrick Ndoli',  role: 'Director, Ejo Heza Partners', color: '#059669', quote: "Our social media went from zero engagement to consistent growth. The content team really understands our audience and our market." },
];

function Testimonials() {
  const [ref, inView] = useInView();
  return (
    <section id="testimonials" className="testimonials section-pad" ref={ref}>
      <div className="container">
        <div className={`sec-header ${inView ? 'in' : ''}`}>
          <span className="sec-label">Testimonials</span>
          <h2>Trusted by Leaders<br />Who Demand More</h2>
        </div>
        <div className={`t-grid ${inView ? 'in' : ''}`}>
          {TESTIMONIALS.map(({ initials, name, role, quote, color }, i) => (
            <div key={name} className="t-card" style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="t-stars">{'★'.repeat(5)}</div>
              <p className="t-quote">"{quote}"</p>
              <div className="t-author">
                <div className="t-avatar" style={{ background: color }}>{initials}</div>
                <div>
                  <p className="t-name">{name}</p>
                  <p className="t-role">{role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Team (#9 human element) ─── */

const SPECIALISTS = [
  { initials: 'GU', name: 'Grace U.',     role: 'Executive Assistant',   exp: '5 yrs experience', color: '#7c3aed', tags: ['Inbox', 'Scheduling', 'Travel'] },
  { initials: 'PM', name: 'Patrick M.',   role: 'Customer Success Lead', exp: '4 yrs experience', color: '#0891b2', tags: ['Support', 'CRM', 'Retention'] },
  { initials: 'CN', name: 'Clarisse N.',  role: 'Content Strategist',    exp: '3 yrs experience', color: '#059669', tags: ['Social', 'Copy', 'SEO'] },
  { initials: 'EK', name: 'Emmanuel K.', role: 'Research Analyst',      exp: '6 yrs experience', color: '#d97706', tags: ['Data', 'Reports', 'Insights'] },
];

function Team() {
  const [ref, inView] = useInView();
  return (
    <section className="team section-pad" ref={ref}>
      <div className="container">
        <div className={`sec-header ${inView ? 'in' : ''}`}>
          <span className="sec-label">Sample Specialists</span>
          <h2>Meet Your Dedicated<br />Specialists</h2>
          <p>Example specialist profiles showing the calibre of talent you'll work with. Your dedicated team is matched to you once you onboard.</p>
        </div>
        <div className={`team-grid ${inView ? 'in' : ''}`}>
          {SPECIALISTS.map(({ initials, name, role, exp, color, tags }, i) => (
            <div key={name} className="team-card" style={{ transitionDelay: `${i * 0.09}s` }}>
              <span className="team-sample-badge">Sample</span>
              <div className="team-avatar-wrap">
                <div className="team-avatar" style={{ background: `${color}18`, color }}>
                  {initials}
                </div>
                <div className="team-online" />
              </div>
              <h4 className="team-name">{name}</h4>
              <p className="team-role">{role}</p>
              <p className="team-exp">{exp}</p>
              <div className="team-tags">
                {tags.map(t => <span key={t} className="team-tag">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
        <p className={`team-note ${inView ? 'in' : ''}`}>
          Sample specialist profiles — real team members are added as you onboard
        </p>
      </div>
    </section>
  );
}

/* ─── CTA ─── */

function CTA() {
  const [ref, inView] = useInView();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (email) { setSent(true); setEmail(''); }
  };

  return (
    <section id="contact" className="cta section-pad" ref={ref}>
      <div className="cta-shimmer" aria-hidden />
      <div className={`cta-box ${inView ? 'in' : ''}`}>
        <span className="sec-label light">Ready to Begin?</span>
        <h2>Let's Build Your<br />Dream Support Team</h2>
        <p>Join businesses and entrepreneurs worldwide already running smoother with Chandelia by their side.</p>
        {sent ? (
          <div className="cta-success">
            <Check size={20} />
            <span>We'll be in touch within 24 hours!</span>
          </div>
        ) : (
          <form className="cta-form" onSubmit={submit}>
            <div className="cta-input-wrap">
              <Mail size={16} className="cta-input-icon" />
              <input
                type="email"
                placeholder="Your business email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-dark">
              Book a Free Call <ArrowRight size={16} />
            </button>
          </form>
        )}
        <p className="cta-note">No commitment · Response within 24 hours · Cancel anytime</p>

        <div className="cta-contact-alts">
          <span className="cta-contact-label">Or reach us directly</span>
          <div className="cta-contact-links">
            <a href="tel:+250789851937" className="cta-contact-item">
              <Phone size={14} /> 0789 851 937
            </a>
            <a href="tel:+250788719669" className="cta-contact-item">
              <Phone size={14} /> 0788 719 669
            </a>
            <a href="mailto:chandeliavaa@gmail.com" className="cta-contact-item">
              <Mail size={14} /> chandeliavaa@gmail.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */

function Footer() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <button className="nav-logo" onClick={() => go('hero')}>
            <span className="logo-gem">◈</span>
            <span className="logo-name light">Chandelia</span>
          </button>
          <p>Premium virtual support services that illuminate your business and unlock growth.</p>
          <div className="footer-contact">
            <span><Mail size={14} /> chandeliavaa@gmail.com</span>
            <span><Phone size={14} /> 0789 851 937</span>
            <span><Phone size={14} /> 0788 719 669</span>
          </div>
        </div>

        <div className="footer-cols">
          <div className="footer-col">
            <h4>Services</h4>
            <button onClick={() => go('services')}>Executive Assistance</button>
            <button onClick={() => go('services')}>Customer Support</button>
            <button onClick={() => go('services')}>Technical Support</button>
            <button onClick={() => go('services')}>Social Media</button>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <button onClick={() => go('how-it-works')}>How It Works</button>
            <button onClick={() => go('pricing')}>Pricing</button>
            <button onClick={() => go('testimonials')}>Testimonials</button>
            <button onClick={() => go('contact')}>Contact Us</button>
          </div>
        </div>
      </div>

      <div className="footer-bar">
        <span>© 2024 Chandelia. All rights reserved.</span>
        <div className="footer-legal">
          <button>Privacy Policy</button>
          <button>Terms of Service</button>
        </div>
      </div>
    </footer>
  );
}

/* ─── WhatsApp floating button ─── */

function WhatsAppButton() {
  const WA_NUMBER = '250789851937';
  const WA_MESSAGE = encodeURIComponent("Hello Chandelia, I'd like to learn more about your virtual assistant services.");
  const href = `https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="wa-btn"
      aria-label="Chat with us on WhatsApp"
    >
      <WhatsAppIcon />
      <span className="wa-label">Chat with us</span>
      <span className="wa-pulse" aria-hidden />
    </a>
  );
}

/* ─── App ─── */

export default function App() {
  return (
    <div className="app">
      <Navbar />
      <Hero />
      <Services />
      <HowItWorks />
      <WhyUs />
      <Pricing />
      <Testimonials />
      <Team />
      <CTA />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
